"use client";

import { PexLogo } from "@pex/design-tokens";
import { ChevronDown, Menu, X } from "lucide-react";
import NextImage from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../ui/navigation-menu";
import { AuthNavControls } from "./auth-nav-controls";
import { NavDrawerAuthFooter } from "./nav-drawer-auth-footer";

const NAV_ICON = {
  info: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/info.svg"
      width={20}
    />
  ),
  chair: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/chair.svg"
      width={20}
    />
  ),
  search: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/search.svg"
      width={20}
    />
  ),
  menuBook: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/menu-book.svg"
      width={20}
    />
  ),
  login: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/login.svg"
      width={20}
    />
  ),
  rocket: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/rocket.svg"
      width={20}
    />
  ),
  libraryBooks: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/library-books.svg"
      width={20}
    />
  ),
  school: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/school.svg"
      width={20}
    />
  ),
  exodus: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/exodus.svg"
      width={20}
    />
  ),
  resound: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/resound.svg"
      width={20}
    />
  ),
  vantage: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/vantage.svg"
      width={20}
    />
  ),
  thrive: (
    <NextImage
      alt=""
      className="shrink-0"
      height={20}
      src="/icons/nav/thrive.svg"
      width={20}
    />
  ),
};

function buildNavData(websiteUrl: string, finderUrl: string) {
  const w = (path: string) => `${websiteUrl}${path}`;
  const onFinder = !!websiteUrl;
  const onWebsite = !!finderUrl;

  return {
    dropdownItems: [
      {
        label: "Find Help",
        children: [
          {
            label: "Overview",
            href: w("/find-help"),
            external: onFinder,
            icon: NAV_ICON.info,
          },
          {
            label: "About Groups",
            href: w("/recovery-groups"),
            external: onFinder,
            icon: NAV_ICON.chair,
          },
          {
            label: "Find a Group",
            href: onWebsite ? finderUrl : "/in-person",
            external: onWebsite,
            icon: NAV_ICON.search,
          },
          {
            label: "About Programmes",
            href: w("/programmes/overview"),
            external: onFinder,
            icon: NAV_ICON.menuBook,
          },
          {
            label: "Join a Programme",
            href: onWebsite ? finderUrl : "/in-person",
            external: onWebsite,
            icon: NAV_ICON.login,
          },
        ],
      },
      {
        label: "Get Involved",
        children: [
          {
            label: "Overview",
            href: w("/partner/overview"),
            external: onFinder,
            icon: NAV_ICON.info,
          },
          {
            label: "Getting Started",
            href: w("/partner/getting-started"),
            external: onFinder,
            icon: NAV_ICON.rocket,
          },
          {
            label: "Recovery Groups",
            href: w("/recovery-groups"),
            external: onFinder,
            icon: NAV_ICON.chair,
          },
          {
            label: "Specialised Programmes",
            href: w("/programmes/overview"),
            external: onFinder,
            icon: NAV_ICON.libraryBooks,
          },
          {
            label: "Academy",
            href: "https://academy.projectexodus.net/",
            external: true,
            icon: NAV_ICON.school,
          },
        ],
      },
      {
        label: "Programmes",
        children: [
          {
            label: "Overview",
            href: w("/programmes/overview"),
            external: onFinder,
            icon: NAV_ICON.info,
          },
          {
            label: "Exodus Recovery Skills",
            href: w("/programmes/exodus-recovery-skills"),
            external: onFinder,
            icon: NAV_ICON.exodus,
          },
          {
            label: "Resound",
            href: w("/programmes/resound"),
            external: onFinder,
            icon: NAV_ICON.resound,
          },
          {
            label: "Vantage",
            href: w("/programmes/vantage"),
            external: onFinder,
            icon: NAV_ICON.vantage,
          },
          {
            label: "Thrive",
            href: w("/programmes/thrive"),
            external: onFinder,
            icon: NAV_ICON.thrive,
          },
        ],
      },
    ],
    directItems: [
      { label: "Resources", href: w("/resources"), external: onFinder },
      { label: "About", href: w("/about"), external: onFinder },
      { label: "Contact", href: w("/contact"), external: onFinder },
      { label: "Donate", href: w("/donate"), external: onFinder },
      {
        label: "Store",
        href: "https://store.projectexodus.net/",
        external: true,
      },
    ],
  };
}

export interface SiteNavbarProps {
  avatarUrl?: string | null;
  className?: string;
  /**
   * Theme key of the app this navbar is rendered in. Forwarded to
   * AuthNavControls so the app switcher can mark the current location active.
   * Defaults to "website" because that's where SiteNavbar originated.
   */
  currentApp?: string;
  finderUrl?: string;
  fixed?: boolean;
  websiteUrl?: string;
}

export function SiteNavbar({
  avatarUrl,
  websiteUrl = "",
  finderUrl = "",
  fixed = false,
  className,
  currentApp = "website",
}: SiteNavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);

  const { dropdownItems, directItems } = useMemo(
    () => buildNavData(websiteUrl, finderUrl),
    [websiteUrl, finderUrl]
  );

  const toggleSection = (label: string) => {
    setOpenSection((prev) => (prev === label ? null : label));
  };

  const closeMenu = () => {
    setMobileOpen(false);
    setOpenSection(null);
  };

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const logoHref = websiteUrl || "/";
  const logoExternal = !!websiteUrl;

  return (
    <>
      <header
        className={cn(
          "z-50 bg-white",
          fixed ? "fixed inset-x-0 top-0" : "relative",
          className
        )}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between pr-2 pl-4 sm:pr-4 sm:pl-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Link
              aria-label="Project Exodus home"
              href={logoHref}
              {...(logoExternal
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
            >
              <PexLogo className="h-9 w-auto" />
            </Link>

            <nav
              aria-label="Main navigation"
              className="hidden items-center gap-1 lg:flex"
            >
              <NavigationMenu viewport={false}>
                <NavigationMenuList>
                  {dropdownItems.map((item) => (
                    <NavigationMenuItem key={item.label}>
                      <NavigationMenuTrigger className="rounded-md bg-transparent px-3 py-2 font-normal text-base hover:bg-accent data-open:bg-accent data-popup-open:bg-accent">
                        {item.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="w-72 p-2">
                          {item.children.map((child) => (
                            <li key={`${child.href}-${child.label}`}>
                              <Link
                                className="flex items-center gap-2.5 rounded-md px-3 py-2 text-base hover:bg-accent"
                                href={child.href}
                                {...(child.external
                                  ? {
                                      target: "_blank",
                                      rel: "noopener noreferrer",
                                    }
                                  : {})}
                              >
                                {child.icon}
                                {child.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ))}

                  {directItems.map((item) => (
                    <NavigationMenuItem key={item.label}>
                      <Link
                        className="rounded-md px-3 py-2 font-normal text-base hover:bg-accent"
                        href={item.href}
                        {...(item.external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {item.label}
                      </Link>
                    </NavigationMenuItem>
                  ))}
                </NavigationMenuList>
              </NavigationMenu>
            </nav>
          </div>

          <div className="hidden items-center lg:flex">
            <AuthNavControls
              avatarUrl={avatarUrl}
              currentApp={currentApp}
              hideThemeToggle
              requiresAuth={false}
            />
          </div>

          <div className="flex items-center lg:hidden">
            <button
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              className="relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-md"
              onClick={() => setMobileOpen((o) => !o)}
              type="button"
            >
              <Menu
                className={cn(
                  "absolute h-5 w-5 transition-all duration-200",
                  mobileOpen
                    ? "rotate-90 scale-0 opacity-0"
                    : "rotate-0 scale-100 opacity-100"
                )}
              />
              <X
                className={cn(
                  "absolute h-5 w-5 transition-all duration-200",
                  mobileOpen
                    ? "rotate-0 scale-100 opacity-100"
                    : "-rotate-90 scale-0 opacity-0"
                )}
              />
            </button>
          </div>
        </div>
      </header>

      <div
        aria-hidden={!mobileOpen}
        className={cn(
          "fixed inset-x-0 top-16 bottom-0 z-40 flex flex-col bg-white transition-all duration-200 ease-out lg:hidden",
          mobileOpen
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0"
        )}
      >
        <nav
          aria-label="Mobile navigation"
          className="flex-1 overflow-y-auto px-2 py-2 sm:px-4"
        >
          {dropdownItems.map((item) => {
            const isOpen = openSection === item.label;
            return (
              <div key={item.label}>
                <button
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left font-normal text-base hover:bg-accent"
                  onClick={() => toggleSection(item.label)}
                  type="button"
                >
                  {item.label}
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                      isOpen && "rotate-180"
                    )}
                  />
                </button>

                <div
                  className={cn(
                    "grid transition-all duration-200 ease-in-out",
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  )}
                >
                  <div className="overflow-hidden">
                    <div className="pb-1 pl-3">
                      {item.children.map((child) => (
                        <Link
                          className="flex items-center gap-3 rounded-md px-3 py-2.5 text-base hover:bg-accent"
                          href={child.href}
                          key={`${child.href}-${child.label}`}
                          onClick={closeMenu}
                          {...(child.external
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : {})}
                        >
                          {child.icon}
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="mt-1 border-border border-t pt-1">
            {directItems.map((item) => (
              <Link
                className="block rounded-md px-3 py-3 font-normal text-base hover:bg-accent"
                href={item.href}
                key={item.label}
                onClick={closeMenu}
                {...(item.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="shrink-0 border-t py-4 pr-3 pl-4">
          <NavDrawerAuthFooter
            avatarUrl={avatarUrl}
            currentApp={currentApp}
            requiresAuth={false}
          />
        </div>
      </div>
    </>
  );
}
